import React, { useMemo, useState } from "react";
import "./style.css";
import { Dropdown, Input, Menu, message, Modal, Row, Table } from "antd";
import CustomPrimaryButton from "../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import { dentalIcons } from "../../../dentail-indetifcation/utils-icons/dentalIcons";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EllipsisOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import {
  errorRequestHandel,
  formateDateAndTime,
  isAdminLogin,
  isValidEmailAddress,
  normalizeSubscription,
  renderSubscriptionStatusTextAndBgColor,
  toTitleCase,
} from "../../../helper";
import { useSelector } from "react-redux";
import CustomModal from "../../../age-assessment/components/modals/CustomModal";
import {
  addUserToMyOrganization,
  deactivateUserInMyOrganization,
  deleteUserFromMyOrganization,
  updateUserInMyOrganization,
} from "../../../components/https/planSubscriptions";
import useIsAdmin from "../../../hooks/useIsAdmin";
import { getSubscriptionState } from "../../../utils/subscriptionUtils";

const UserManagement = ({ onRefetch }) => {
  const isAdmin = useIsAdmin();

  const myOrganization = useSelector((state) => state.general?.myOrganization);

  const normalizedSub = normalizeSubscription(
    myOrganization?.active_subscription
  );

  const subscriptionState = getSubscriptionState(normalizedSub);
  const privileges = useSelector((state) => state.auth?.privileges);
  const userdata = useSelector(
    (state) => state.settings_data.profileSettings_data
  );
  const users = myOrganization?.examiners;

  const totalSeats = normalizedSub?.noOfSeats ?? 0;
  const activeSeats = myOrganization?.examiners?.length ?? 0;
  const availableSeats = totalSeats - activeSeats;

  const [searchKey, setSearchKey] = useState("");
  const [editRecord, setEditRecord] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState({
    name: "",
    email: "",
    role: "employee",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
  });

  const createUser = async (user) => {
    try {
      const payload = {
        first_name: user?.name,
        email: user?.email,
        role: user?.role,
      };
      setIsLoading(true);
      const response = await addUserToMyOrganization({ payload: payload });
      if (response.status === 200) {
        message.success("User has been added successfully");
        setIsLoading(false);
        setUserModalOpen(false);
        setValues({
          ...values,
          name: "",
          email: "",
        });
        onRefetch();
      }
    } catch (error) {
      setIsLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  const updateUser = async (id, updatedData) => {
    try {
      const payload = {
        id: id,
        first_name: updatedData?.name,
        email: updatedData?.email,
        role: updatedData?.role,
        is_active: editRecord?.is_active,
      };
      setIsLoading(true);
      const response = await updateUserInMyOrganization({ payload: payload });
      if (response.status === 200) {
        message.success("User has been updated successfully");
        setIsLoading(false);
        setUserModalOpen(false);
        setValues({
          ...values,
          name: "",
          email: "",
        });
        onRefetch();
      }
    } catch (error) {
      setIsLoading(false);
      errorRequestHandel({ error: error });
    }
  };

  const handleAddNewUser = async () => {
    const isEmailValid = isValidEmailAddress(values.email);
    if (values.name === "" && values.email === "") {
      setErrors({
        ...errors,
        name: "This field is required",
        email: "This field is required",
      });
      return;
    }
    if (!isEmailValid) {
      setErrors({
        ...errors,
        email: "Invalid email address",
      });
      return;
    }
    if (recordId) {
      await updateUser(recordId, values);
    } else {
      await createUser(values);
    }
  };

  const deleteUser = async (id) => {
    try {
      const response = await deleteUserFromMyOrganization({ id: id });
      if (response.status === 200) {
        message.success("User has been deleted successfully");
        setUserModalOpen(false);
        onRefetch();
      }
    } catch (error) {
      errorRequestHandel({ error: error });
    }
  };

  const handleEditUser = (record) => {
    setEditRecord(record);
    setValues({
      ...values,
      email: record?.email,
      name: record?.first_name + " " + record?.last_name,
      role: record?.role,
    });
    setUserModalOpen(true);
    setRecordId(record?.id);
  };
  const toggleUserStatus = async (record) => {
    try {
      const payload = {
        id: record?.id,
        first_name: record?.first_name,
        last_name: "",
        email: record?.email,
        role: record?.role, // admin, employee
        is_active: !record.is_active,
      };

      const response = await deactivateUserInMyOrganization({
        payload: payload,
      });
      if (response.status === 200) {
        message.success(
          record?.is_active
            ? "User has been deactivate successfully"
            : "User has been activated successfully"
        );
        onRefetch();
      }
    } catch (error) {
      errorRequestHandel({ error: error });
    }
  };

  const handleToggleUserStatus = (record) => {
    const isActive = record?.is_active;
    Modal.confirm({
      icon: null, // disables default icon
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ExclamationCircleOutlined
            style={{ fontSize: 22, color: "#faad14" }}
          />
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            {isActive ? "Deactivate User" : "Activate User"}
          </span>
        </div>
      ),
      content: (
        <div style={{ paddingTop: 10 }}>
          <p style={{ marginBottom: 8, fontSize: 15 }}>
            {isActive
              ? "Are you sure you want to deactivate this user?"
              : "Are you sure you want to activate this user?"}
          </p>
          <p style={{ marginBottom: 0, fontSize: 14, color: "#888" }}>
            {isActive
              ? "The user will lose access immediately but can be reactivated later."
              : "The user will regain access immediately upon activation."}
          </p>
        </div>
      ),
      okText: isActive ? "Yes, Deactivate" : "" + "Yes, Activate",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: isActive ? "#rgb(0, 179, 102)" : "#20c3ff",
          borderColor: "transparent",
        },
      },
      width: 420,
      onOk: () => toggleUserStatus(record),
    });
  };
  const handleDelete = (id) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ExclamationCircleOutlined
            style={{ fontSize: 22, color: "#faad14" }}
          />
          <span style={{ fontSize: 18, fontWeight: 600 }}>Delete User</span>
        </div>
      ),
      content: (
        <div style={{ paddingTop: 10 }}>
          <p style={{ marginBottom: 8, fontSize: 15 }}>
            Are you sure you want to delete this user?
          </p>
          <p style={{ marginBottom: 0, fontSize: 14, color: "#888" }}>
            This action cannot be undone and will free up the assigned seat.
          </p>
        </div>
      ),
      okText: "Yes, Delete",
      cancelText: "Cancel",
      okButtonProps: {
        style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" },
      },
      width: 420,
      onOk: () => deleteUser(id),
    });
  };

  // Filtered data based on search key
  const filteredUsers = useMemo(() => {
    if (!searchKey.trim()) return users;

    return users.filter((user) => {
      const fullName = `${user.first_name || ""} ${
        user.last_name || ""
      }`.toLowerCase();
      const email = user.email?.toLowerCase() || "";

      return (
        fullName.includes(searchKey.toLowerCase()) ||
        email.includes(searchKey.toLowerCase())
      );
    });
  }, [searchKey, users]);

  return (
    <div className="user_management_container">
      <div className="user_management_body">
        <div className="user_management_table_section">
          <div className="user_management_table_heading">
            <p className="_title">Users Management</p>
          </div>
          {/* <div className="user_management_table_count_section">
            <div className="_count_section">
              <p className="_title">Active</p>
              <p className="_count">{activeSeats}</p>
            </div>
            <div className="_count_section">
              <p className="_title">Pending</p>
              <p className="_count">{availableSeats}</p>
            </div>
            <div className="_count_section">
              <p className="_title">Total</p>
              <p className="_count">{totalSeats}</p>
            </div>
          </div> */}
        </div>
        <Row
          gutter={[10, 10]}
          justify={"space-between"}
          style={{ padding: "0 5px" }}
        >
          <div className="search_section">
            <div className="search_section_field">
              <img src={dentalIcons.searchIcon} />
              <Input
                placeholder="Search"
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
              />
            </div>
          </div>
          {isAdmin &&
            subscriptionState.canManageUsers &&
            users.length < totalSeats && (
              <CustomPrimaryButton
                title="Add New User"
                icon={dentalIcons.plusButtonIcon}
                leadingIcon={true}
                onClick={() => setUserModalOpen(true)}
                isDisbale={users.length === totalSeats}
                width={200}
              />
            )}
        </Row>
        <br />
        <Table
          dataSource={filteredUsers}
          className="_rounded_table"
          pagination={false}
          columns={[
            {
              title: "Name",
              key: "id",

              render: (text, record) => {
                return (
                  <div className="custom_table_row_data">
                    <p className="_title" style={{ fontWeight: "bold" }}>
                      {record.first_name + " " + record.last_name}
                    </p>
                  </div>
                );
              },
            },
            {
              title: "Email",
              key: "email",
              dataIndex: "email",
            },

            {
              title: "Added At",
              key: "created_at",

              render: (text, record) =>
                formateDateAndTime(record?.created_at, "DD MMM YYYY"),
            },
            {
              title: <div className="custom_header_col">Role</div>,
              key: "role",
              render: (text, record) => (
                <div className="center_align_digits">
                  <div className={`_role_tag ${record?.role}`}>
                    <p>{record?.role === "admin" ? "Admin" : "User"}</p>
                  </div>
                </div>
              ),
            },
            {
              title: <div className="custom_header_col">Status</div>,
              key: "status",
              dataIndex: "status",
              render: (text, record) => {
                const statusObject = renderSubscriptionStatusTextAndBgColor(
                  record?.is_active ? "active" : "inActive"
                );
                return (
                  <div className="center_align_digits">
                    <div
                      className="status_with_dot"
                      style={{ background: statusObject.bgColor }}
                    >
                      <div
                        className="_dot"
                        style={{ background: statusObject.textColor }}
                      />
                      <p style={{ color: statusObject.textColor }}>
                        {statusObject?.status}
                      </p>
                    </div>
                  </div>
                );
              },
            },

            {
              title: <div className="custom_header_col">Actions</div>,
              key: "actions",
              render: (text, record) => {
                const isSelf = record?.id === userdata?.id;

                if (isSelf) return null; // Hide actions for your own row
                const menu = (
                  <Menu className="eclips_menu">
                    {}
                    <Menu.Item
                      key="edit"
                      onClick={() => handleEditUser(record)}
                    >
                      <div className="eclips_section">
                        <img src={dentalIcons.editIcon} />
                        <p>Edit</p>
                      </div>
                    </Menu.Item>
                    {record?.is_active ? (
                      <Menu.Item
                        key="edit"
                        onClick={() => handleToggleUserStatus(record)}
                      >
                        <div className="eclips_section">
                          <CloseCircleTwoTone
                            twoToneColor="#ff4d4f"
                            style={{ fontSize: 18 }}
                          />
                          <p>Deactivate User</p>
                        </div>
                      </Menu.Item>
                    ) : activeSeats <= totalSeats ? (
                      <Menu.Item
                        key="edit"
                        onClick={() => handleToggleUserStatus(record)}
                      >
                        <div className="eclips_section">
                          <CheckCircleTwoTone
                            twoToneColor="#52c41a"
                            style={{ fontSize: 18 }}
                          />
                          <p>Activate User</p>
                        </div>
                      </Menu.Item>
                    ) : null}

                    <Menu.Item
                      key="delete"
                      onClick={() => handleDelete(record?.id)}
                    >
                      <div className="eclips_section">
                        <img src={dentalIcons.deleteIcon} />
                        <p style={{ color: "red" }}>Delete</p>
                      </div>
                    </Menu.Item>
                  </Menu>
                );

                return (
                  <div className="center_align_digits">
                    <Dropdown overlay={menu} trigger={["click"]}>
                      <EllipsisOutlined
                        style={{ fontSize: "20px", cursor: "pointer" }}
                      />
                    </Dropdown>
                  </div>
                );
              },
            },
          ]}
        />
      </div>

      <CustomModal
        open={userModalOpen}
        width={400}
        onCancel={() => setUserModalOpen(false)}
      >
        <div className="add_new_user_mask_container">
          <div className="add_new_user_mask_body">
            <div className="add_new_user_header">
              <div className="add_new_user_icon">
                <img src={dentalIcons.seatsIcon} />
              </div>
              <p className="_title">Add User</p>
            </div>
            <div className="add_new_user_form">
              <div className="add_new_user_form_field">
                <p className="_label">Name</p>
                <Input
                  className="_input"
                  placeholder="Enter name of user"
                  onFocus={() => setErrors({ ...errors, name: "" })}
                  onChange={(e) =>
                    setValues({ ...values, name: e.target.value })
                  }
                  value={values?.name}
                />
                {errors?.name && <p className="_error">{errors.name}</p>}
              </div>
              <div className="add_new_user_form_field">
                <p className="_label">Email</p>
                <Input
                  className="_input"
                  placeholder="Enter email of user"
                  onFocus={() => setErrors({ ...errors, email: "" })}
                  onChange={(e) =>
                    setValues({ ...values, email: e.target.value })
                  }
                  value={values?.email}
                />
                {errors?.email && <p className="_error">{errors.email}</p>}
              </div>
              <div className="add_new_user_form_field">
                <p className="_label">Select Role</p>
                <div className="_role">
                  <div
                    className="_tag"
                    onClick={() => setValues({ ...values, role: "admin" })}
                    style={{
                      color: values?.role === "admin" ? "#0A9FD4" : "#5c5858",
                      border:
                        values.role === "admin"
                          ? "1px solid #0A9FD4"
                          : "1px solid #dfdfdf",
                      background:
                        values.role === "admin" ? "#20C3FF1A" : "transparent",
                    }}
                  >
                    <p>Admin</p>
                  </div>
                  <div
                    className="_tag"
                    onClick={() => setValues({ ...values, role: "employee" })}
                    style={{
                      color:
                        values?.role === "employee" ? "#0A9FD4" : "#5c5858",
                      border:
                        values.role === "employee"
                          ? "1px solid #0A9FD4"
                          : "1px solid #dfdfdf",
                      background:
                        values.role === "employee"
                          ? "#20C3FF1A"
                          : "transparent",
                    }}
                  >
                    <p>User</p>
                  </div>
                </div>
              </div>
              <div className="_button">
                <CustomPrimaryButton
                  title="Save"
                  width={"100%"}
                  onClick={handleAddNewUser}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default UserManagement;
